import Modal from '@components/elements/modal/modal';
import {
  assertSome,
  Flag,
  isNone,
  isSome,
  Maybe,
  noop,
  useFlag,
  v2_useBranch,
  v2_useCreateNewCart,
  v2_useDeliveryAvailability,
  v2_useProductList,
  v2_getProductTitles,
} from '@crocodille/common';
import { getDayOfWeek } from '@crocodille/util';
import cn from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analyticsEvent } from 'src/modules/analytics';
import * as routing from '../../../routing';
import AddressSearch, {
  AddressSearchResult,
} from '../../address-search/address-search';
import Error from '../../elements/error/error';
import BranchCarousel from '../branch-carousel/branch-carousel';
import styles from './shipping-courier-form.module.scss';
import Button from '@components/elements/button/button';
import { getAddToCartError } from '@crocodille/bbdomu-i18n';
import { useAlert } from '@components/alert/alert';

export interface ShippingCourierFormProps {
  navigateToProductList?(): void;
  readonly isFormFocused?: boolean;
}

const ShippingCourierForm: React.FC<
  React.PropsWithChildren<ShippingCourierFormProps>
> = ({ navigateToProductList = noop, isFormFocused = false }) => {
  const { t } = useTranslation();
  const isInWebView = useFlag(Flag.IS_IN_WEBVIEW);
  const [suggestion, setSuggestion] =
    useState<Maybe<AddressSearchResult>>(null);

  const addressDataRef = useRef(suggestion);
  addressDataRef.current = suggestion;

  const addressDataId =
    suggestion && 'id' in suggestion ? suggestion?.id : null;

  const {
    data: deliveryAvailability,
    error: deliveryAvailabilityError,
    isValidating: isValidatingAvailability,
  } = v2_useDeliveryAvailability(addressDataId);

  const isDeliveryAvailable = deliveryAvailability?.deliveryAvailable ?? false;

  const dispatchAlert = useAlert();
  const { data: products } = v2_useProductList();

  const { trigger: createNewCart, pendingCart } = v2_useCreateNewCart(
    (cart) => {
      analyticsEvent('add_shipping_info', {
        ecommerce: {
          shipping_tier: `${cart.shippingMethodName} - ${cart.branchCode}`,
        },
      });

      navigateToProductList();
    },
    {
      onError: (e) => {
        if (e && typeof e === 'object') {
          if ('kind' in e && typeof e.kind === 'string') {
            dispatchAlert(
              t(getAddToCartError(e.kind), {
                products:
                  e.kind === 'product_unavailable' && products
                    ? ` (${v2_getProductTitles(
                        products,
                        (e as unknown as { productVariantCodes: string[] })
                          .productVariantCodes,
                      ).join(', ')})`
                    : null,
              }),
              t(getAddToCartError('other')),
            );
          }
        }
      },
    },
  );

  const handleSubmitEvent = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (suggestion?.role === 'gate' && suggestion?.gate) {
        await createNewCart({
          branchCode: suggestion.code,
          delivery: {
            deliveryMethod: 'gate',
            gate: suggestion.gate,
          },
        });
      } else if (addressDataId) {
        await createNewCart({
          branchCode: assertSome(
            deliveryAvailability?.branchProximity?.[0]?.branchCode,
          ),
          delivery: {
            deliveryMethod: 'courier',
            addressDataId,
          },
        });
      }
    },
    [createNewCart, suggestion, addressDataId, deliveryAvailability],
  );

  const isValid =
    isSome(suggestion) &&
    ((isSome(addressDataId) && isDeliveryAvailable) || isNone(addressDataId));
  const isBusy = isValidatingAvailability || isSome(pendingCart);
  const error = deliveryAvailability?.message ?? deliveryAvailabilityError;

  const dayOfWeek = getDayOfWeek(new Date());

  const cancelShippingChoiceForBranch = useCallback(() => {
    setSuggestion(null);
  }, [setSuggestion]);

  const { data: suggestedBranch } = v2_useBranch(
    suggestion && suggestion.role === 'branch' ? suggestion.code : false,
  );

  const showShippingChoiceForBranch =
    suggestion && suggestion.role === 'branch' ? suggestedBranch : null;

  return (
    <>
      <form className={styles.container} onSubmit={handleSubmitEvent}>
        <div className={styles.fields}>
          <AddressSearch
            onAddressPicked={setSuggestion}
            pickedAddress={suggestion}
            queryGeolocation
            suggestSaved
            suggestBranches
            isInputFocused={isFormFocused}
          />
          <Button
            type="submit"
            round
            size="large"
            disabled={isSome(suggestion) && (!isValid || isBusy)}
            className={cn(styles.submitButton, {
              [styles.isInWebView]: isInWebView,
            })}
          >
            <span className={styles.visibleDesktop}>
              {t('shipping.begin_order')}
            </span>
          </Button>
        </div>
        {isSome(error) ? <Error>{error.toString?.()}</Error> : null}
      </form>
      <Modal
        open={isSome(showShippingChoiceForBranch)}
        showCloseButton
        onClose={cancelShippingChoiceForBranch}
        title={t('shipping.change_to_restaurant')}
        width="auto"
      >
        {isSome(showShippingChoiceForBranch) ? (
          <>
            <p className={styles.modalText}>
              {t('shipping.change_to_restaurant_hint')}
            </p>
            <BranchCarousel.Item
              as="div"
              key={showShippingChoiceForBranch.code}
              code={showShippingChoiceForBranch.code}
              label={showShippingChoiceForBranch.name}
              address={showShippingChoiceForBranch.address}
              availability={showShippingChoiceForBranch.availability}
              openFrom={
                showShippingChoiceForBranch.openingHours[dayOfWeek]?.openFrom
              }
              openTo={
                showShippingChoiceForBranch.openingHours[dayOfWeek]?.openTo
              }
              openingHours={showShippingChoiceForBranch.openingHours}
              email={showShippingChoiceForBranch.email}
              phoneNumber={showShippingChoiceForBranch.phoneNumber}
              deliveryFrom={
                showShippingChoiceForBranch.supportedShippingMethodTypes.includes(
                  'delivery',
                )
                  ? showShippingChoiceForBranch.openingHours[dayOfWeek]
                      ?.deliveryFrom
                  : null
              }
              deliveryTo={
                showShippingChoiceForBranch.supportedShippingMethodTypes.includes(
                  'delivery',
                )
                  ? showShippingChoiceForBranch.openingHours[dayOfWeek]
                      ?.deliveryTo
                  : null
              }
              detailHref={routing.branch({
                code: showShippingChoiceForBranch.code,
              })}
              showDetailButton={false}
              detailOpen={false}
              onDetailOpen={noop}
              onShippingMethodPicked={async (branchCode, shippingMethodId) => {
                await createNewCart({
                  branchCode,
                  delivery: {
                    deliveryMethod: 'atBranch',
                    shippingMethodId,
                  },
                });

                cancelShippingChoiceForBranch();
              }}
              shippingMethods={showShippingChoiceForBranch.inRestaurantShippingMethods.map(
                (shippingMethod) => ({
                  id: shippingMethod.id,
                  label: shippingMethod.name,
                  disabled:
                    (isSome(pendingCart) &&
                      pendingCart.delivery.deliveryMethod === 'atBranch' &&
                      pendingCart.delivery.shippingMethodId ===
                        shippingMethod.id) ||
                    shippingMethod.disabled,
                }),
              )}
              properties={showShippingChoiceForBranch.properties}
            />
          </>
        ) : null}
      </Modal>
    </>
  );
};

export default ShippingCourierForm;
